// de-front/de-order-admin/src/components/purchase-sales/detail-general-tab.tsx
import { Box, Divider, Grid, Group, Input, Stack, Text, Textarea } from "@mantine/core"
import { CalendarIcon, MapPinIcon, UserIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next"
import { IOrderPlus } from "../../models/Order"
import { dateFormat } from "../../utils/date"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import classes from "./detail-general-tab.module.css"

type Props = {
  data: IOrderPlus
}

export function DetailGeneralTab({ data }: Props) {
  const { t } = useTranslation(["order", "supplierSales", "common"])

  const buyer = data.senderBusiness
  const supplier = data.receiverBusiness
  const formatPerson = (u?: { firstName?: string | null; lastName?: string | null } | null) =>
    renderName(u)

  return (
    <Stack gap="md">
      {/* Partner info */}
      <Box>
        <Text className={classes.sectionLabel}>
          {t("supplierSales:purchase.detail.general.partnerInfo")}
        </Text>
        <Grid mt={8}>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Input.Wrapper label={t("order:fields.partnerName")}>
              <Text className={classes.value}>
                {buyer?.refCode ? `${buyer.refCode}, ` : ""}
                {renderBusinessName(buyer)}
              </Text>
            </Input.Wrapper>
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Input.Wrapper label={t("order:fields.clientName")}>
              <Text className={classes.value}>
                {supplier?.refCode ? `${supplier.refCode}, ` : ""}
                {renderBusinessName(supplier)}
              </Text>
            </Input.Wrapper>
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Input.Wrapper label={t("order:fields.taxNumber")}>
              <Text className={classes.value}>{buyer?.regNumber || "-"}</Text>
            </Input.Wrapper>
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Input.Wrapper label={t("order:fields.paymentTerm")}>
              <Text className={classes.value}>{data.paymentTerm?.description || "-"}</Text>
            </Input.Wrapper>
          </Grid.Col>
        </Grid>
      </Box>

      <Divider color="var(--mantine-color-gray-2)" />

      {/* Managers */}
      <Box>
        <Text className={classes.sectionLabel}>
          {t("supplierSales:purchase.detail.general.managers")}
        </Text>
        <Grid mt={8}>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Input.Wrapper label={t("order:fields.buyerManager")}>
              <Group gap="xs" mt={5}>
                <UserIcon size={14} color="var(--mantine-color-gray-6)" />
                <Text className={classes.value}>
                  {formatPerson((buyer as any)?.buyerStaff)}
                </Text>
              </Group>
            </Input.Wrapper>
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Input.Wrapper label={t("order:fields.supplierManager")}>
              <Group gap="xs" mt={5}>
                <UserIcon size={14} color="var(--mantine-color-gray-6)" />
                <Text className={classes.value}>
                  {formatPerson((supplier as any)?.supplierStaff)}
                </Text>
              </Group>
            </Input.Wrapper>
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Input.Wrapper label={t("order:fields.receiverStaff")}>
              <Group gap="xs" mt={5}>
                <UserIcon size={14} color="var(--mantine-color-gray-6)" />
                <Text className={classes.value}>{formatPerson(data.receiverStaff)}</Text>
              </Group>
            </Input.Wrapper>
          </Grid.Col>
        </Grid>
      </Box>

      <Divider color="var(--mantine-color-gray-2)" />

      {/* Delivery */}
      <Box>
        <Text className={classes.sectionLabel}>
          {t("supplierSales:purchase.detail.general.deliveryInfo")}
        </Text>
        <Grid mt={8}>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Input.Wrapper label={t("order:fields.receiverBranch")}>
              <Text className={classes.value}>{data.receiverBranch?.name || "-"}</Text>
            </Input.Wrapper>
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Input.Wrapper label={t("order:fields.receiverAddress")}>
              <Group gap="xs" mt={5}>
                <MapPinIcon size={14} color="var(--mantine-color-gray-6)" />
                <Text className={classes.value}>
                  {data.receiverBranch?.branchAddress || "-"}
                </Text>
              </Group>
            </Input.Wrapper>
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Input.Wrapper label={t("order:fields.deliveryType")}>
              <Group gap="xs" mt={5}>
                <CalendarIcon size={14} color="var(--mantine-color-gray-6)" />
                <Text className={classes.value}>
                  {data.deliveryType === "CUSTOM_DATE"
                    ? t("order:fields.customDate")
                    : t("order:fields.scheduledDate")}
                </Text>
              </Group>
            </Input.Wrapper>
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Input.Wrapper label={t("order:fields.deliveryDate")}>
              <Group gap="xs" mt={5}>
                <CalendarIcon size={14} color="var(--mantine-color-gray-6)" />
                <Text className={classes.value}>{data.deliveryDate ? dateFormat(data.deliveryDate) : "-"}</Text>
              </Group>
            </Input.Wrapper>
          </Grid.Col>
        </Grid>
      </Box>

      {(data.senderNote || data.senderAdditionalNote) && (
        <>
          <Divider color="var(--mantine-color-gray-2)" />
          <Box>
            <Text className={classes.sectionLabel}>
              {t("supplierSales:purchase.detail.general.notes")}
            </Text>
            <Grid mt={8}>
              {data.senderNote && (
                <Grid.Col span={{ xs: 12, md: 6 }}>
                  <Textarea
                    readOnly
                    label={t("order:purchaseSales.customerNote")}
                    value={data.senderNote}
                    autosize
                    minRows={2}
                  />
                </Grid.Col>
              )}
              {data.senderAdditionalNote && (
                <Grid.Col span={{ xs: 12, md: 6 }}>
                  <Textarea
                    readOnly
                    label={t("order:purchaseSales.enterNote")}
                    value={data.senderAdditionalNote}
                    autosize
                    minRows={2}
                  />
                </Grid.Col>
              )}
            </Grid>
          </Box>
        </>
      )}
    </Stack>
  )
}

